/**
 * Sticker taxonomy and the slug -> kind resolver.
 *
 * These are plain data and a pure function, deliberately kept OUT of
 * ServiceSticker.tsx. That module is `'use client'`, which makes every one of its
 * exports a client reference — importing `getServiceStickerKind` from it into a
 * server component type-checks and builds, then throws at request time with
 * "Attempted to call getServiceStickerKind() from the server but
 * getServiceStickerKind is on the client". The quotation preview is a server
 * component and needs to resolve a sticker while rendering, so the resolver has
 * to live in a module with no client boundary.
 *
 * ServiceSticker re-exports these for the client components that already import
 * them from there; server code must import from this file directly.
 */

export type ServiceStickerKind =
    | 'creative-studio'
    | 'growth-systems'
    | 'paid-ads-performance'
    | 'strategy-brand-building';

export type ServiceStickerVariant = 'blue' | 'red' | 'green' | 'orange';

export const serviceStickerOptions = [
    { label: 'Creative Studio', value: 'creative-studio' },
    { label: 'Growth Systems', value: 'growth-systems' },
    { label: 'Paid Ads & Performance', value: 'paid-ads-performance' },
    { label: 'Strategy & Brand Building', value: 'strategy-brand-building' },
] as const;

export const serviceStickerSlugMap: Record<string, ServiceStickerKind> = {
    'marketing-strategy': 'strategy-brand-building',
    'social-media-content': 'creative-studio',
    'photography-videography-cgi': 'creative-studio',
    'branding-visual-identity': 'strategy-brand-building',
    'web-development-solutions': 'growth-systems',
    'seo-geo-visibility': 'paid-ads-performance',
    'paid-advertising-campaigns': 'paid-ads-performance',
    'marketing-technology-automation': 'growth-systems',
};

export const defaultVariantByKind: Record<ServiceStickerKind, ServiceStickerVariant> = {
    'creative-studio': 'blue',
    'growth-systems': 'red',
    'paid-ads-performance': 'orange',
    'strategy-brand-building': 'orange',
};

export function getServiceStickerKind(
    slug?: string | null,
    fallback: ServiceStickerKind = 'strategy-brand-building',
) {
    if (!slug) return fallback;
    return serviceStickerSlugMap[slug] || fallback;
}
